import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { useMeEmailChangePending, useV1MeRetrieve } from '@/api/client'
import { PageHeader } from '@/components/page-header'
import { AvatarPortrait } from '@/components/profile/dicebear-avatar'
import { AvatarCreatorDialog } from '@/components/profile/avatar-creator-dialog'
import { ChangePasswordDialog } from '@/components/profile/change-password-dialog'
import { DisplayNameDialog } from '@/components/profile/display-name-dialog'
import { EmailChangeDialog } from '@/components/profile/email-change-dialog'
import { ProfileSection } from '@/components/profile/profile-section'
import { SessionsSection } from '@/components/profile/sessions-section'
import { TotpApprovalsCard, TotpExchangeCard } from '@/components/totp-exchange'
import { avatarSeed } from '@/lib/avatar-catalog'

export const Route = createFileRoute('/_panel/profil')({
  component: ProfilePage,
})

/**
 * "Mój profil" (TR-300): the account's current state in read-only sections,
 * each modification reachable only through its section's entry point. The
 * change-history section is dropped (TR-305 withdrawn). Wave B (backend 029):
 * display-name edit (TR-301), e-mail change with a pending-confirmation state
 * (TR-303) and the per-session list with revocation (TR-300 R5/R6).
 */
function ProfilePage() {
  const { data: me } = useV1MeRetrieve()
  // The regenerated client types the response as a 200/401 union — narrow by status.
  const user = me?.status === 200 ? me.data : undefined
  // StaffMe carries `email`; StudentMe does not. The panel only ever renders
  // staff (students are redirected in the _panel guard).
  const email = user && 'email' in user ? user.email : undefined

  const [nameSaved, setNameSaved] = useState(false)
  const [avatarSaved, setAvatarSaved] = useState(false)
  const [emailChangeStarted, setEmailChangeStarted] = useState(false)

  // TR-303: oczekująca zmiana adresu — sekcja e-mail pokazuje stan „czeka na
  // potwierdzenie" także po przeładowaniu strony.
  const pendingQuery = useMeEmailChangePending()
  const pendingChange =
    pendingQuery.data?.status === 200 ? pendingQuery.data.data.pending : undefined

  return (
    <>
      <PageHeader title="Mój profil" />
      <div className="flex w-full max-w-3xl flex-col gap-6 p-6">
        <ProfileSection
          title="Dane identyfikacyjne"
          description="Nazwa wyświetlana prezentowana innym użytkownikom aplikacji."
          action={
            user && <DisplayNameDialog current={user.nickname} onSaved={() => setNameSaved(true)} />
          }
        >
          {user?.nickname ? (
            <p className="text-sm">{user.nickname}</p>
          ) : (
            // TR-300: brak wartości → wartość zastępcza + wyeksponowany punkt
            // wejścia do jej ustawienia (FR-005; przycisk „Ustaw nazwę" wyżej).
            <p className="text-sm text-muted-foreground">Nie ustawiono nazwy wyświetlanej.</p>
          )}
          {nameSaved && (
            <p className="text-sm text-green-700">Nazwa wyświetlana została zapisana.</p>
          )}
        </ProfileSection>

        <ProfileSection
          title="Awatar"
          description="Awatar prezentowany przy Twoim koncie."
          action={<AvatarCreatorDialog onSaved={() => setAvatarSaved(true)} />}
        >
          <div className="flex items-center gap-4">
            {/* alt pusty: nagłówek sekcji i nazwa konta niosą tę samą tożsamość. */}
            <AvatarPortrait
              options={user?.avatar}
              seed={avatarSeed(email ?? 'loading')}
              alt=""
              size="lg"
            />
            {/* 054: konto, które nigdy nie zapisało konfiguracji, renderuje domyślny
                wygląd z biblioteki, więc tu zawsze coś widać. */}
            <p className="text-sm text-muted-foreground">
              Awatar złożysz w kreatorze z elementów udostępnionych przez operatora.
            </p>
          </div>
          {avatarSaved && <p className="text-sm text-green-700">Awatar został zapisany.</p>}
        </ProfileSection>

        <ProfileSection
          title="Adres e-mail konta"
          description="Bieżący, potwierdzony adres powiązany z kontem."
          action={
            email && (
              <EmailChangeDialog
                currentEmail={email}
                onStarted={() => setEmailChangeStarted(true)}
              />
            )
          }
        >
          <p className="text-sm">{email ?? '…'}</p>
          {emailChangeStarted && (
            <p className="text-sm text-green-700">
              Rozpoczęto zmianę adresu e-mail. Sprawdź skrzynkę nowego adresu.
            </p>
          )}
          {pendingChange && (
            // TR-303 R7: adres konta pozostaje niezmieniony do potwierdzenia.
            // Backend celowo zwraca adres oczekujący wyłącznie zamaskowany
            // (kontrakt 029 — pełna wartość nie wraca po inicjacji).
            <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">
              Zmiana adresu czeka na potwierdzenie. Link potwierdzający wysłano na adres{' '}
              <span className="font-medium">{pendingChange.new_email_masked}</span> (ważny do{' '}
              {new Date(pendingChange.expires_at).toLocaleString('pl-PL')}). Ponowne rozpoczęcie
              zmiany unieważni ten link.
            </p>
          )}
        </ProfileSection>

        <ProfileSection
          title="Bezpieczeństwo konta"
          description="Hasło i drugi składnik uwierzytelniania."
        >
          <ChangePasswordDialog />
        </ProfileSection>

        {user && 'has_totp' in user && user.has_totp && <TotpExchangeCard />}
        {user?.role === 'superadmin' && <TotpApprovalsCard />}

        <SessionsSection />
      </div>
    </>
  )
}
